import { type ReactNode, useState } from "react";
import { Trash2 } from "lucide-react";
import { DragHandle, dragChrome } from "@/components/ui/DragHandle";
import { IconButton } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Menu, MenuGroup, MenuItem } from "@/components/ui/Menu";
import { Panel } from "@/components/ui/Panel";
import { Thumbnail } from "@/components/ui/Thumbnail";
import type { FigureResponse } from "@patent-studio/api-types";
import { isPending } from "@/hooks/use-figures";
import { cn } from "@/lib/utils";
import type { ArrangementView } from "@/components/shared/arrangement-drag";
import {
  movedIntoGroup,
  movedToPosition,
  movedOutOfGroup,
  useArrangement,
  useFigureSortable,
} from "@/components/shared/arrangement-drag";
import { laneOf } from "@/hooks/use-figure-layout";
import { namedGroup } from "@/lib/figure-group-naming";
import { named } from "@/lib/figure-naming";
import { FigurePreview } from "./FigurePreview";
import { figureName } from "@/v2/figures/DeletedFigures";

/**
 * One figure, drawn the same wherever it sits.
 *
 * A figure standing on its own and a figure inside a group are one card in the
 * design, which is what lets a drag carry it into a group without it changing
 * shape on the way. Fixed width rather than filling its container, so a group
 * can lay its figures out side by side and the top level does not stretch them.
 */
export const CARD_SHAPE = "flex w-[320px] max-w-full items-center gap-2.5";
const CARD_CLASS = `${CARD_SHAPE} bg-muted/40`;

function FigureCard({
  figure,
  handleRef,
  onDelete,
  options,
}: {
  figure: FigureResponse;
  handleRef?: (element: Element | null) => void;
  onDelete: (fileId: string) => void;
  /** The row's Options, beside its Remove: where else the figure can be put. */
  options?: ReactNode;
}) {
  const waiting = isPending(figure);
  const description = named(figure);

  const thumbnail = <Thumbnail src={figure.thumbnail_url} alt={description} />;

  return (
    <>
      {handleRef === undefined ? (
        <span className="size-4 flex-none" />
      ) : (
        <DragHandle
          size="sm"
          handleRef={handleRef}
          data-testid={`figure-grip-${figure.file_id}`}
          label={`Reorder ${description}`}
        />
      )}

      {/* No preview until the server confirms the figure: its image is an
          object URL revoked the moment the upload settles. */}
      {waiting ? (
        thumbnail
      ) : (
        <FigurePreview figure={figure}>{thumbnail}</FigurePreview>
      )}

      <div className="flex min-w-0 flex-1 flex-col gap-0.5 overflow-hidden">
        <span className="flex items-center gap-1.5">
          <span className="font-mono text-xs font-bold">
            {waiting
              ? "Uploading"
              : figure.label === null
                ? "Not placed"
                : `FIG. ${figure.label}`}
          </span>
          {figure.source === "intake" && (
            <span className="flex-none rounded-sm border border-border bg-muted px-1.5 py-0.5 text-[9.5px] font-bold whitespace-nowrap text-brand-start">
              Added during intake
            </span>
          )}
        </span>
        <span className="truncate font-mono text-[11.5px] text-muted-foreground">
          {figure.original_filename}
        </span>
      </div>

      {!waiting && options}
      {!waiting && (
        <IconButton
          variant="secondary"
          size="dense"
          type="button"
          aria-label={`Remove ${figureName(figure.label, figure.original_filename)}`}
          title="Remove figure"
          onClick={() => {
            onDelete(figure.file_id);
          }}
          className="text-muted-foreground hover:border-destructive/40 hover:text-destructive"
        >
          <Trash2 className="size-3.5" />
        </IconButton>
      )}
    </>
  );
}

/**
 * A figure that may be moved: the drag machinery attaches here.
 *
 * The lane names which list the figure currently sits in, which is what lets a
 * drag carry it from the top level into a group and back out again.
 */
export function SortableFigureRow({
  figure,
  index,
  lane,
  onDelete,
  landing = false,
}: {
  figure: FigureResponse;
  index: number;
  lane: string;
  onDelete: (fileId: string) => void;
  /** Whether a release right now would land on this card. */
  landing?: boolean;
}) {
  const { ref, handleRef, isDragging } = useFigureSortable(
    figure.file_id,
    index,
    lane,
  );

  return (
    <Panel
      as="li"
      shape="row"
      pad="tight"
      ref={ref}
      data-testid={`figure-row-${figure.file_id}`}
      className={cn(
        CARD_CLASS,
        // The card a release would land on is marked, so the landing place is
        // read off the screen rather than predicted from the pointer.
        dragChrome({ dragging: isDragging, landing }),
      )}
    >
      <FigureCard
        figure={figure}
        handleRef={handleRef}
        onDelete={onDelete}
        options={<MoveOptions figure={figure} lane={lane} />}
      />
      {isDragging && <span className="sr-only">Moving</span>}
    </Panel>
  );
}

/**
 * Where else a figure can be put, for an attorney who does not drag: into any group
 * it is not in, and out of the group it is in, to just after it. Written as a drop
 * would write it, so the numbers follow as they would after a drag.
 */
function MoveOptions({
  figure,
  lane,
}: {
  figure: FigureResponse;
  lane: string;
}) {
  const view = useArrangement();
  if (view === null) return null;
  const places = lane in view.lanes ? view.lanes[lane].length : 0;
  if (places < 2 && view.groups.length === 0) return null;
  return (
    <Menu name={`Options for ${named(figure)}`}>
      <MoveChoices figure={figure} lane={lane} view={view} places={places} />
    </Menu>
  );
}

/** What the Options offer: a place to send the figure to, and the groups it can join or leave. */
function MoveChoices({
  figure,
  lane,
  view,
  places,
}: {
  figure: FigureResponse;
  lane: string;
  view: ArrangementView;
  places: number;
}) {
  const [asking, setAsking] = useState(false);
  const { lanes, groups, acts } = view;
  if (asking) {
    return (
      <PositionField
        places={places}
        onMove={(position) => {
          acts.arrange(movedToPosition(lanes, figure.file_id, position));
        }}
      />
    );
  }
  const home = groups.find((group) => laneOf(group.group_id) === lane);
  const others = groups.filter((group) => group !== home);
  return (
    <>
      {places > 1 && (
        <MenuItem
          title="Move to position…"
          description={
            home === undefined
              ? "A group counts as one place."
              : `Among the figures of ${namedGroup(home)}.`
          }
          keepOpen
          onSelect={() => {
            setAsking(true);
          }}
        />
      )}
      {home !== undefined && (
        <MenuItem
          title="Move out of group"
          description={`To just after ${namedGroup(home)}`}
          onSelect={() => {
            acts.arrange(movedOutOfGroup(lanes, figure.file_id, home.group_id));
          }}
        />
      )}
      {others.length > 0 && (
        <MenuGroup heading="Move into group">
          {others.map((group) => (
            <MenuItem
              key={group.group_id}
              title={group.title === "" ? "Untitled group" : group.title}
              onSelect={() => {
                acts.arrange(
                  movedIntoGroup(lanes, figure.file_id, group.group_id),
                );
              }}
            />
          ))}
        </MenuGroup>
      )}
    </>
  );
}

/** The place to send a figure to, counted from 1 and held to the list's ends. */
function PositionField({
  places,
  onMove,
}: {
  places: number;
  onMove: (position: number) => void;
}) {
  const [typed, setTyped] = useState("");
  const position = Math.min(
    Math.max(Math.round(Number(typed) || 1), 1),
    places,
  );
  return (
    <div className="flex flex-col gap-1 p-1">
      <Input
        type="number"
        size="snug"
        min={1}
        max={places}
        autoFocus
        aria-label={`Position, 1 to ${String(places)}`}
        value={typed}
        onChange={(event) => {
          setTyped(event.target.value);
        }}
      />
      <MenuItem
        title={`Move to position ${String(position)}`}
        onSelect={() => {
          onMove(position);
        }}
      />
    </div>
  );
}

/** A figure that may not be moved, because the server has not confirmed it. */
export function StaticFigureRow({
  figure,
  onDelete,
}: {
  figure: FigureResponse;
  onDelete: (fileId: string) => void;
}) {
  return (
    <Panel
      as="li"
      shape="row"
      pad="tight"
      data-testid={`figure-row-${figure.file_id}`}
      className={CARD_CLASS}
    >
      <FigureCard figure={figure} onDelete={onDelete} />
    </Panel>
  );
}
